import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import TouchableElement from '@components/common/touchableElement';
import NcFileContent from '@components/common/NCFile';
import { SmallLoading } from '@components/loading';

import { ignoreProductionInformation, setChangeProductionInformation, setSetupProductionInformation, setSelectedNCFile } from '@reducers/manufacture';
import { isPhone, withERP } from '@helpers';
import ProductionInformation from '@components/productionInformation';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import NCFileWizardNoERP from './NCFileWizardNoERP';
import { isSimpleType, TYPE_DESCRIPTIONS } from './NCFileWizardNoERP/constants';
import { NC_FILE_TYPE_OPTIONS } from './constants';
import { setShouldShowGlobalFullNCFileButton } from '@reducers/machinesV2';

export const Icon = ({ name, color, C }) => {
  return (
    <View
      style={{
        width: scale(100),
        height: scale(36),
        borderRadius: scale(SCREEN_BORDER_RADIUS),
        backgroundColor: color,
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: scale(3),
      }}
    >
      <Text style={[font['Body'], { color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t(name)}</Text>
    </View>
  );
};
export const MoreInfo = ({ selectedType, C }) => {
  if (selectedType === 'linked') {
    return (
      <View style={{ width: '100%', paddingHorizontal: scale(15), paddingVertical: scale(16) }}>
        <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]}>{i18n.t('Linked')}</Text>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginTop: scale(12), paddingRight: scale(100) }]}>
          {i18n.t('When an NC File is Linked, the Machine Status can have 3 colors:')}
        </Text>
        <View style={{ marginTop: scale(22), flexDirection: 'row' }}>
          <Icon {...{ name: 'Active', color: C('Status/Light/Active'), C }} />
          <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary'), width: scale(184), marginLeft: scale(14) }]}>
            {i18n.t('Machine is running in automatic mode')}
          </Text>
        </View>
        <View style={{ marginTop: scale(30), flexDirection: 'row' }}>
          <Icon {...{ name: 'Change', color: C('Status/Light/Change'), C }} />
          <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary'), width: scale(184), marginLeft: scale(14) }]}>
            {i18n.t('Machine is idle, finished or stopped, after being Active')}
          </Text>
        </View>
        <View style={{ marginTop: scale(30), flexDirection: 'row' }}>
          <Icon {...{ name: 'Setup', color: C('Status/Light/Setup'), C }} />
          <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary'), width: scale(184), marginLeft: scale(14) }]}>
            {i18n.t('Machine is not running and was not Active before')}
          </Text>
        </View>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginTop: scale(11) }]}>
          {i18n.t('You can add production information to an Linked NC File like runtime, change time and setup time')}
        </Text>
      </View>
    );
  }
  if (selectedType === 'change') {
    return (
      <View style={{ width: '100%', paddingHorizontal: scale(15), paddingVertical: scale(16) }}>
        <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]}>{i18n.t('Change')}</Text>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginTop: scale(12), paddingRight: scale(64) }]}>
          {i18n.t(
            'In some configurations a specific program or subprogram is called to trigger a workpiece change If this is the case, assign the NC File Type “Change” to this NC File'
          )}
        </Text>
      </View>
    );
  }
  if (selectedType === 'setup') {
    return (
      <View style={{ width: '100%', paddingHorizontal: scale(15), paddingVertical: scale(16) }}>
        <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]}>{i18n.t('Setup')}</Text>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginTop: scale(12), paddingRight: scale(64) }]}>
          {i18n.t(
            'When this NC File runs, the machine is being prepared for production — for example tool measurement, zero-point calibration or fixture probing. Assign the NC File Type “Setup” to this NC File'
          )}
        </Text>
      </View>
    );
  }
  if (selectedType === 'ignore') {
    return (
      <View style={{ width: '100%', paddingHorizontal: scale(15), paddingVertical: scale(16) }}>
        <Text style={[font['Headline'], { color: C('Label/Light/Primary') }]}>{i18n.t('Ignore')}</Text>
        <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary'), marginTop: scale(12), paddingRight: scale(64) }]}>
          {i18n.t(
            'Select Ignore if you don’t want to add Production Information to this NC File Starts and Stops of this NC File will be ignored and will not change the status of a machine'
          )}
        </Text>
        <Text style={[font['Headline'], { color: C('Label/Light/Primary'), marginTop: scale(31) }]}>{i18n.t('Examples of Ignored NC Files')}</Text>
        <Text style={[font['Body'], { color: C('Label/Light/Primary'), marginTop: scale(12), paddingRight: scale(64) }]}>{i18n.t('Subprogram')}</Text>
        <Text style={[font['Body'], { color: C('Label/Light/Primary'), marginTop: scale(12), paddingRight: scale(64) }]}>
          {i18n.t('Shell program')}
        </Text>
        <Text style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), paddingRight: scale(64) }]}>
          {i18n.t('Sometime used for automation')}
        </Text>
      </View>
    );
  }

  return null;
};

const NCFileTypeERP = (props) => {
  const {
    C,
    selectedItem,
    hide,
    ignoreProductionInformation,
    productionBatchId,
    setChangeProductionInformation,
    setSetupProductionInformation,
    ncFileId: _ncFileId,
    ncFileName: _ncFileName,
    setShouldShowGlobalFullNCFileButton,
  } = props;
  const [selectedType, setSelectedType] = useState('linked');
  const [loading, setLoading] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [showProductionInfo, setShowProductionInfo] = useState(false);
  const [isNcFileContentAvailable, setIsNcFileContentAvailable] = useState(false);

  const ncFileId = _ncFileId ?? selectedItem?.machineStatus?.ncFileId;
  const ncFileName = _ncFileName ?? selectedItem?.machineStatus?.ncFile ?? selectedItem?.machineStatus?.ncFileName;

  const [leftWidth, setLeftWidth] = useState(0);
  const [rightWidth, setRightWidth] = useState(0);
  const [maxWidth, setMaxWidth] = useState(0);
  const [isInitialMeasurement, setIsInitialMeasurement] = useState(true);

  useEffect(() => {
    if (isInitialMeasurement && leftWidth > 0 && rightWidth > 0) {
      const newMaxWidth = Math.max(leftWidth, rightWidth);
      setMaxWidth(newMaxWidth);
      setIsInitialMeasurement(false);
    }
  }, [leftWidth, rightWidth, isInitialMeasurement]);

  useEffect(() => {
    setShouldShowGlobalFullNCFileButton(false);
    return () => setShouldShowGlobalFullNCFileButton(true);
  }, []);

  const nextName = (() => {
    if (selectedType === 'linked') return 'Next';
    if (isSimpleType(selectedType)) return 'Done';
    return '';
  })();

  const done = () => {
    setLoading(false);
    hide();
  };

  const submitAction = async () => {
    const data = {
      ncFileId,
    };

    switch (selectedType) {
      case 'linked': {
        setShowProductionInfo(true);
        return;
      }
      case 'change': {
        setLoading(true);
        await setChangeProductionInformation(data);
        done();
        return;
      }
      case 'ignore': {
        setLoading(true);
        await ignoreProductionInformation(data);
        done();
        return;
      }
      case 'setup': {
        setLoading(true);
        await setSetupProductionInformation(data);
        done();
        return;
      }
      default:
        return '';
    }
  };

  if (showProductionInfo) {
    return (
      <View style={isPhone ? { flex: 1 } : { height: '100%' }}>
        <ProductionInformation
          {...{
            selectedNCFileId: ncFileId,
            onBackPress: () => setShowProductionInfo(false),
            hide,
            productionBatchId,
            isNcFileContentAvailable,
          }}
        />
      </View>
    );
  }

  return (
    <View testID={TEST_IDS.popups.linkNcFileWithERP} style={{ height: '100%', backgroundColor: C('Background/Light/Panel') }}>
      <View
        style={{
          width: '100%',
          zIndex: 1000,
          height: scale(43),
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <View
          onLayout={(event) => {
            if (isInitialMeasurement) {
              setLeftWidth(event.nativeEvent.layout.width);
            }
          }}
          style={[{ flexShrink: 0 }, maxWidth && { width: maxWidth }]}
        >
          <TouchableElement
            onPress={() => {
              hide();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(12), paddingBottom: 6, flexDirection: 'row' }]}>
                <Text numberOfLines={1} style={[{ color: C(pressHoverColor('System/Light/Blue')), marginLeft: scale(7) }, font.Body]}>{i18n.t('Cancel')}</Text>
              </View>
            )}
          </TouchableElement>
        </View>

        <View
          style={{
            flex: 1,
            minWidth: 0,
            paddingTop: scale(12),
            paddingHorizontal: scale(6),
            paddingBottom: scale(3),
            opacity: isInitialMeasurement ? 0 : 1,
          }}
        >
          <Text
            numberOfLines={1}
            style={[
              {
                color: C('Label/Light/Primary'),
                textAlign: 'center',
                width: '100%',
              },
              font['Headline Large'],
            ]}
          >
            {i18n.t('Select_NC_File_Type_Header')}
          </Text>
        </View>

        <View
          onLayout={(event) => {
            if (isInitialMeasurement) {
              setRightWidth(event.nativeEvent.layout.width);
            }
          }}
          style={[{ flexShrink: 0, alignItems: 'flex-end' }, maxWidth && { width: maxWidth }]}
        >
          <TouchableElement
            onPress={() => {
              if (loading) {
                return;
              }
              submitAction();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { padding: scale(12), paddingBottom: 6 }]}>
                {loading ? (
                  <SmallLoading />
                ) : (
                  <Text numberOfLines={1} style={[{ color: C(pressHoverColor('Link/Light/Primary')), marginRight: scale(7) }, font.Headline]}>{i18n.t(nextName)}</Text>
                )}
              </View>
            )}
          </TouchableElement>
        </View>
      </View>
      <View style={{ width: '100%', zIndex: 100 }}>
        <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Footnote]}>{ncFileName}</Text>
      </View>
      <View style={{ paddingHorizontal: scale(15), marginTop: scale(20), width: '100%', marginHorizontal: 'auto', flex: 1 }}>
        <Text style={[font.Footnote, { color: C('Label/Light/Tertiary'), paddingLeft: scale(15), marginBottom: scale(5) }]}>
          {i18n.t('SELECT THE NC FILE TYPE')}
        </Text>
        <View
          style={{
            backgroundColor: C('Background/Light/Field lighter'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            width: '100%',
            flexDirection: 'row',
            padding: scale(18),
          }}
        >
          {NC_FILE_TYPE_OPTIONS.map((t) => {
            const selected = selectedType === t.icon;
            return (
              <View key={t.name} style={{ width: '25%', justifyContent: 'center' }}>
                <TouchableElement
                  onPress={() => {
                    if (loading) {
                      return;
                    }
                    setSelectedType(t.icon);
                  }}
                  hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                >
                  {({ boxStyle, pressHoverColor }) => (
                    <View
                      style={[
                        boxStyle,
                        {
                          width: '100%',
                          alignItems: 'center',
                          paddingVertical: scale(18),
                          borderWidth: scale(2),
                          borderRadius: scale(SCREEN_BORDER_RADIUS),
                          borderColor: selected ? C('Selected/Light/Primary') : 'transparent',
                        },
                      ]}
                    >
                      <Svg icon={`${t.icon}${selected ? 'Active' : 'Inactive'}`} width={scale(48)} height={scale(48)} />
                      <Text numberOfLines={1} style={[{ color: C('Label/Light/Primary'), marginVertical: scale(7) }, font['Subhead 1']]}>{i18n.t(t.name)}</Text>
                      <Svg
                        icon={selected ? 'selected' : 'unselected'}
                        width={scale(22)}
                        height={scale(22)}
                        color={selected ? 'Selected / Light / Primary' : 'Label/Light/Quinary'}
                      />
                    </View>
                  )}
                </TouchableElement>
              </View>
            );
          })}
        </View>
        <View style={{ paddingHorizontal: scale(15), marginTop: scale(6) }}>
          <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t(TYPE_DESCRIPTIONS[selectedType])}</Text>
          {!showMoreInfo && (
            <TouchableElement
              onPress={() => {
                setShowMoreInfo(true);
              }}
              hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
            >
              {({ boxStyle }) => (
                <View style={[boxStyle, {}]}>
                  <Text style={[{ color: C('Link/Light/Primary') }, font.Footnote]}>{i18n.t('More info')}</Text>
                </View>
              )}
            </TouchableElement>
          )}
        </View>
        <View
          style={[
            {
              minWidth: '100%',
              width: '100%',
              paddingTop: scale(15),
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            },
          ]}
        >
          {showMoreInfo ? (
            <View style={{ flex: 1, width: '100%' }}>
              <View style={{ backgroundColor: C('Background/Light/Panel'), borderRadius: scale(SCREEN_BORDER_RADIUS) }}>
                <View
                  style={{
                    zIndex: 100,
                    position: 'absolute',
                    right: scale(9),
                    top: scale(9),
                  }}
                >
                  <TouchableElement
                    onPress={() => {
                      setShowMoreInfo(false);
                    }}
                    hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  >
                    {({ boxStyle }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            backgroundColor: C('Button/Light/Secondary'),
                            width: scale(27),
                            height: scale(27),
                            borderRadius: scale(27) / 2,
                            justifyContent: 'center',
                            alignItems: 'center',
                          },
                        ]}
                      >
                        <Svg icon="close" width={scale(11)} height={scale(11)} color={'Button/Light/Secondary Text'} />
                      </View>
                    )}
                  </TouchableElement>
                </View>
                <MoreInfo {...{ selectedType, C }} />
              </View>
            </View>
          ) : (
            <NcFileContent
              {...{
                selectedItem,
                C,
                font,
                backgroundColor: C('Background/Light/Panel'),
                ncFileId,
                ncFileName,
                showNcFileButtonCallBackOnPress: hide,
                setIsNcFileContentAvailable,
              }}
            />
          )}
        </View>
      </View>
    </View>
  );
};

const NCFileType = (props) => {
  const {
    C,
    selectedItem,
    hide,
    productionBatchId,
    membership,
    ncFileId: _ncFileId,
    ncFileName: _ncFileName,
  } = props;

  const ncFileId = _ncFileId ?? selectedItem?.machineStatus?.ncFileId;
  const ncFileName = _ncFileName ?? selectedItem?.machineStatus?.ncFile ?? selectedItem?.machineStatus?.ncFileName;

  if (!withERP(membership?.isWithERP)) {
    return (
      <NCFileWizardNoERP
        C={C}
        ncFileId={ncFileId}
        ncFileName={ncFileName}
        hide={hide}
        productionBatchId={productionBatchId}
        selectedItem={selectedItem}
      />
    );
  }

  return <NCFileTypeERP {...props} />;
};

export default connect(({ membership }) => ({ membership }), {
  ignoreProductionInformation,
  setChangeProductionInformation,
  setSetupProductionInformation,
  setSelectedNCFile,
  setShouldShowGlobalFullNCFileButton,
})(ColorWrapper(NCFileType));
